import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Link, useLocation } from "wouter";
import { PLANS, type Billing as BillingState, type Plan } from "../../../shared/types.ts";
import { Button, StatusBadge, cn, ease, type Tone } from "../../ui/index.ts";
import { errorText, getBilling, openPortal, startCheckout } from "../../lib/api.ts";
import { useApp } from "../../lib/ctx.ts";
import { day, num } from "../../lib/format.ts";
import { forgetPlan, paidPlan, pickedPlan, settle, type Paid } from "../../lib/plan.ts";
import { Loading } from "../parts.tsx";
import { Notice, Row, SettingsCard } from "./parts.tsx";
import { above } from "./Upgrade.tsx";

const STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: "Active", tone: "mint" },
  trialing: { label: "Active", tone: "mint" },
  past_due: { label: "Payment due", tone: "red" },
  unpaid: { label: "Payment due", tone: "red" },
  incomplete: { label: "Payment due", tone: "red" },
};

/** Price and daily pace; the pace only when it is tighter than the month's allowance. */
function terms(p: Plan): string {
  const { price, monthly, daily } = PLANS[p];
  return [price ? `$${price}/month` : "", daily < monthly ? `${num(daily)} applications a day` : ""].filter(Boolean).join(", ");
}

/** The Billing settings section: plan, this month's usage, upgrades, and the Stripe portal. */
export function Billing({ title, description }: { title: string; description: string }) {
  const app = useApp();
  const [, navigate] = useLocation();
  const [checkout] = useState(() => new URLSearchParams(location.search).get("checkout"));
  const [want] = useState(() => (checkout === "success" ? paidPlan(new URLSearchParams(location.search).get("plan")) : null));
  const [picked] = useState(pickedPlan);
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<Paid | "portal" | null>(null);

  const load = () => {
    setFailed(false);
    return getBilling().then(setBilling, () => setFailed(true));
  };

  useEffect(() => {
    let alive = true;
    forgetPlan();
    if (checkout) navigate("/settings/billing", { replace: true });
    if (checkout === "cancel") app.toast("Checkout canceled.");
    settle(getBilling, want).then(
      (b) => {
        if (!alive) return;
        setBilling(b);
        if (checkout === "success") app.toast(b.plan === want ? `You're on ${PLANS[b.plan].name}.` : "Payment received. Your plan updates in a minute.", "ok");
      },
      () => alive && setFailed(true),
    );
    const back = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      setBusy(null);
      load();
    };
    addEventListener("pageshow", back);
    return () => {
      alive = false;
      removeEventListener("pageshow", back);
    };
  }, []);

  const go = (action: Paid | "portal") => {
    setBusy(action);
    (action === "portal" ? openPortal() : startCheckout(action)).then(
      ({ url }) => location.assign(url),
      (e) => {
        setBusy(null);
        app.toast(errorText(e), "bad");
        load();
      },
    );
  };

  const plan = billing?.plan ?? "free";
  const status = billing?.status ? STATUS[billing.status] : undefined;
  const full = billing ? billing.used >= billing.limit : false;
  const upgrades = above(plan);
  const lead = picked && upgrades.includes(picked) ? picked : upgrades[0];

  const notice = billing && (
    status?.tone === "red" ? (
      <Notice>Your last payment didn't go through. Update your card in Manage billing.</Notice>
    ) : billing.cancelAtPeriodEnd ? (
      <Notice>
        {PLANS[plan].name} ends {day(billing.periodEnd)}. After that you're on Free.
      </Notice>
    ) : full ? (
      <Notice>
        You've used this month's {num(billing.limit)} applications. {upgrades.length ? "Upgrade to keep applying." : `More on ${day(billing.periodEnd)}.`}
      </Notice>
    ) : null
  );

  return (
    <SettingsCard
      title={title}
      description={description}
      notice={notice || undefined}
      footer={
        billing?.hasCustomer && (
          <Button variant="secondary" loading={busy === "portal"} disabled={busy !== null} onClick={() => go("portal")}>
            Manage billing
          </Button>
        )
      }
    >
      {failed ? (
        <Row label="Plan">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-ink-soft">Billing didn't load.</span>
            <Button variant="secondary" size="sm" onClick={() => load()}>
              Try again
            </Button>
          </div>
        </Row>
      ) : !billing ? (
        <div className="px-5 py-4 md:px-6 md:py-5">
          <Loading row label={checkout === "success" ? "Confirming your plan" : "Your plan"} />
        </div>
      ) : (
        <>
          <Row label="Plan">
            <div className="flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[15px] font-bold text-ink">{PLANS[plan].name}</span>
              {status && <StatusBadge tone={status.tone}>{status.label}</StatusBadge>}
              <span className="text-sm text-muted tabular-nums">{terms(plan)}</span>
            </div>
          </Row>
          <Row label="This month" hint={`${plan === "free" ? "Resets" : billing.cancelAtPeriodEnd ? "Ends" : "Renews"} ${day(billing.periodEnd)}`}>
            <div className="flex flex-col gap-2 pt-2.5">
              <p className="text-sm text-ink-soft tabular-nums">
                <span className="font-bold text-ink">{num(billing.used)}</span> of {num(billing.limit)} applications used
              </p>
              <div className="h-2 overflow-hidden rounded-full bg-sunk" role="presentation">
                <motion.div
                  className={cn("h-full rounded-full", full ? "bg-bad" : "bg-primary")}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, (billing.used / Math.max(1, billing.limit)) * 100)}%` }}
                  transition={{ duration: 0.5, ease }}
                />
              </div>
            </div>
          </Row>
          {upgrades.length > 0 && (
            <Row
              label="Upgrade"
              hint={
                <Link href="/upgrade" className="font-semibold text-primary-ink underline-offset-2 hover:underline">
                  Compare plans
                </Link>
              }
            >
              <ul className="flex flex-col gap-2">
                {upgrades.map((p) => (
                  <li key={p} className="flex items-center justify-between gap-4 rounded-control px-4 py-3 ring-1 ring-line ring-inset">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-ink">
                        {PLANS[p].name} <span className="font-semibold text-muted tabular-nums">${PLANS[p].price}/month</span>
                      </p>
                      <p className="mt-0.5 text-[13px] text-muted tabular-nums">{num(PLANS[p].monthly)} applications a month</p>
                    </div>
                    <Button variant={p === lead ? "primary" : "secondary"} size="sm" loading={busy === p} disabled={busy !== null} onClick={() => go(p)}>
                      Upgrade
                    </Button>
                  </li>
                ))}
              </ul>
            </Row>
          )}
        </>
      )}
    </SettingsCard>
  );
}
